import { NJIconButton } from '@engie-group/fluid-design-system-react';
import { UMIcon } from '../../urbanmap/_components';
import { PLAN, UMR } from '../proposition-c/tokens';
import { fmt } from '../proposition-c/ui';
import { CardMenu, OwnerLine } from './People';
import type { ProjectCardItem } from './types';

export type CardAction = 'ouvrir' | 'hypotheses' | 'plans' | 'dupliquer' | 'archiver' | 'restaurer' | 'supprimer';

/** Hauteur commune des cartes — partagée avec le connecteur de famille pour rester aligné. */
export const CARD_HEIGHT = 236;

const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** `2027-07` → `juil. 2027`. */
function monthLabel(value: string): string {
  const [year, month] = value.split('-');
  return `${MONTHS[Number(month) - 1] ?? month} ${year}`;
}

export function contractLabel(contract: ProjectCardItem['contract']): string {
  return `${monthLabel(contract.start)} → ${monthLabel(contract.end)}`;
}

/** Cœur des favoris : plein et coloré quand le projet est en favori, pour que le retrait soit évident. */
function FavoriteButton({ project, onToggle }: { project: ProjectCardItem; onToggle: () => void }) {
  const on = project.favorite;
  return (
    <span
      style={{ display: 'inline-flex', color: on ? PLAN.accent : UMR.textSecondary }}
      title={on ? `Retirer « ${project.name} » de mes favoris` : `Ajouter « ${project.name} » à mes favoris`}
    >
      <NJIconButton
        icon={on ? 'favorite' : 'favorite_border'}
        label={on ? `Retirer « ${project.name} » de mes favoris` : `Ajouter « ${project.name} » à mes favoris`}
        variant="tertiary"
        scale="xs"
        onClick={onToggle}
      />
    </span>
  );
}

/** Ligne de filiation : projet d'origine (avec son nombre de copies) ou copie d'un projet. */
function Lineage({ project, parentName }: { project: ProjectCardItem; parentName?: string }) {
  const isCopy = !!project.parentId;
  const label = isCopy
    ? `Copie de « ${parentName ?? 'projet d’origine'} »`
    : project.copyCount > 0
      ? `Projet d’origine · ${project.copyCount} copie${project.copyCount > 1 ? 's' : ''}`
      : 'Projet d’origine';
  return (
    <span
      title={label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 6px', fontSize: 11, lineHeight: '16px',
        maxWidth: '100%', minWidth: 0, boxSizing: 'border-box',
        border: `1px solid ${isCopy ? UMR.border : PLAN.accentBorder}`, background: isCopy ? UMR.white : PLAN.accentBg,
        color: isCopy ? UMR.textSecondary : PLAN.accentStrong,
      }}
    >
      <UMIcon name={isCopy ? 'content_copy' : 'account_tree'} size={12} color={isCopy ? UMR.textSecondary : PLAN.accent} />
      {/* L'ellipse ne s'applique pas sur un conteneur flex : le libellé a son propre bloc. */}
      <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </span>
  );
}

export function ProjectCard({
  project, parentName, onAction, onToggleFavorite,
}: {
  project: ProjectCardItem;
  parentName?: string;
  onAction: (action: CardAction) => void;
  onToggleFavorite: () => void;
}) {
  const isNew = project.kind === 'new';
  /** Le picto du nom dit si le projet crée un réseau ou développe un réseau existant. */
  const kindIcon = isNew ? 'add_circle' : 'hub';
  const kindLabel = isNew
    ? 'Nouveau réseau'
    : `Réseau existant · ${project.networkNames.join(', ')}`;

  return (
    <div
      className="nj-card"
      style={{ width: 260, minWidth: 260, height: CARD_HEIGHT, flexShrink: 0, overflow: 'hidden', opacity: project.archived ? 0.72 : 1 }}
    >
      <div className="nj-card__body" style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, height: 46, minHeight: 46, flexShrink: 0 }}>
          <span title={kindLabel} style={{ display: 'inline-flex', paddingTop: 6 }}>
            <UMIcon name={kindIcon} size={20} color={isNew ? PLAN.accent : UMR.blue} />
          </span>
          <a
            href="#"
            title={`Ouvrir « ${project.name} » — ${kindLabel} · contrat ${contractLabel(project.contract)}`}
            style={{
              flex: 1, minWidth: 0, fontSize: '0.8rem', lineHeight: '19px', padding: '4px 0',
              color: UMR.textStrong, textDecoration: 'none', overflowWrap: 'anywhere',
              display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden',
            }}
            onClick={(event) => {
              event.preventDefault();
              onAction('ouvrir');
            }}
          >
            {project.name}
          </a>
          <FavoriteButton project={project} onToggle={onToggleFavorite} />
        </div>

        <div
          style={{
            height: 60, minHeight: 60, flexShrink: 0, boxSizing: 'border-box',
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: 4,
            padding: '0 10px', background: UMR.grey100, overflow: 'hidden', minWidth: 0,
          }}
        >
          <Lineage project={project} parentName={parentName} />
          {project.archived && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, lineHeight: '16px', color: UMR.textSecondary, maxWidth: '100%', minWidth: 0 }}>
              <UMIcon name="archive" size={12} color={UMR.textSecondary} />
              <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Projet archivé</span>
            </span>
          )}
          <span style={{ fontSize: 12, lineHeight: '16px', color: UMR.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {project.perimeterLabel} · {fmt(project.buildingCount)} bâtiments
          </span>
        </div>

        <OwnerLine owner={project.owner} members={project.members} />

        <div style={{ marginTop: 4, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, lineHeight: '16px', color: UMR.textSecondary, minWidth: 0 }}>
          <UMIcon name="route" size={14} color={project.planCount > 0 ? PLAN.accent : UMR.textSecondary} />
          <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <strong style={{ color: project.planCount > 0 ? UMR.textStrong : UMR.textSecondary, fontWeight: project.planCount > 0 ? 700 : 400 }}>
              {project.planCount}
            </strong>
            {' '}
            plan{project.planCount > 1 ? 's' : ''} de développement
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginTop: 'auto', flexShrink: 0, minWidth: 0 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.8, lineHeight: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: UMR.textSecondary }}>
            {project.updatedAt}
          </span>
          {!project.archived && (
            <>
              <NJIconButton icon="tune" label={`Hypothèses · ${project.name}`} variant="tertiary" scale="xs" onClick={() => onAction('hypotheses')} />
              <NJIconButton icon="route" label={`Plans de développement · ${project.name}`} variant="tertiary" scale="xs" onClick={() => onAction('plans')} />
            </>
          )}
          <CardMenu
            label={`Autres actions · ${project.name}`}
            items={
              project.archived
                ? [
                  { icon: 'unarchive', label: 'Sortir de l’archive', onSelect: () => onAction('restaurer') },
                  { icon: 'delete', label: 'Supprimer le projet', danger: true, onSelect: () => onAction('supprimer') },
                ]
                : [
                  { icon: 'content_copy', label: 'Dupliquer le projet', onSelect: () => onAction('dupliquer') },
                  { icon: 'archive', label: 'Archiver le projet', onSelect: () => onAction('archiver') },
                  { icon: 'delete', label: 'Supprimer le projet', danger: true, onSelect: () => onAction('supprimer') },
                ]
            }
          />
        </div>
      </div>
    </div>
  );
}
